import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_MY_APPOINTMENTS } from '../graphql/clientQueries';
import { VideoConferencesSection } from '../components/VideoConferencesSection';
import type { Appointment } from '../types';

interface GetMyAppointmentsResponse {
  listMyAppointments: Appointment[];
}

/**
 * CalendarScreen - Client appointments calendar view
 * 
 * Displays:
 * - Month view calendar at top with appointment dots
 * - Upcoming appointments list below
 * - Collapsible past appointments section at bottom
 */
export function CalendarScreen() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pastExpanded, setPastExpanded] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // Fetch appointments for a wide range to cover past and future
  const dateRange = useMemo(() => {
    const start = new Date();
    start.setMonth(start.getMonth() - 6);
    const end = new Date();
    end.setMonth(end.getMonth() + 6);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, []);

  const { data, loading, error, refetch } = useQuery<GetMyAppointmentsResponse>(GET_MY_APPOINTMENTS, {
    variables: dateRange,
    fetchPolicy: 'cache-and-network',
  });

  const appointments = data?.listMyAppointments ?? [];
  const now = new Date();

  // Split appointments into upcoming and past
  const { upcoming, past } = useMemo(() => {
    const upcoming: Appointment[] = [];
    const past: Appointment[] = [];
    
    appointments.forEach(apt => {
      if (new Date(apt.startTime) >= now) {
        upcoming.push(apt);
      } else {
        past.push(apt);
      }
    });

    // Sort upcoming by start time (earliest first)
    upcoming.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    // Sort past by start time (most recent first)
    past.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
    
    return { upcoming, past };
  }, [appointments, now]);

  // Get appointments for a specific date (for calendar dots)
  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.startTime);
      return aptDate.toDateString() === date.toDateString();
    });
  };

  // Calendar navigation
  const navigateMonth = (direction: number) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(new Date());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days: (Date | null)[] = [];

    // Add padding for days before the first of the month
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }

    return days;
  }, [currentMonth]);

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString();
  const isSelected = (date: Date) => date.toDateString() === selectedDate.toDateString();

  // Get appointments for selected date
  const selectedDateAppointments = useMemo(() => {
    return appointments
      .filter(apt => {
        const aptDate = new Date(apt.startTime);
        return aptDate.toDateString() === selectedDate.toDateString();
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [appointments, selectedDate]);

  const formatSelectedDate = (date: Date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 flex flex-col lg:h-[calc(100vh-10rem)] lg:min-h-[300px]">
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-aalaw-red border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 flex flex-col lg:h-[calc(100vh-10rem)] lg:min-h-[300px]">
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium mb-2">Unable to load calendar</p>
            <button onClick={() => refetch()} className="text-aalaw-red hover:underline text-sm">Try again</button>
          </div>
        </div>
      </div>
    );
  }

  // Render appointment list item
  const renderAppointmentItem = (apt: Appointment, isPastItem: boolean = false) => (
    <button
      key={apt.id}
      onClick={() => setSelectedAppointment(apt)}
      className={`w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors ${isPastItem ? 'opacity-75' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 text-center">
          <div className={`text-xs ${isPastItem ? 'text-gray-400' : 'text-gray-500'}`}>
            {isPastItem 
              ? new Date(apt.startTime).toLocaleDateString([], { month: 'short', day: 'numeric' })
              : formatDate(apt.startTime)
            }
          </div>
          <div className={`text-sm font-semibold ${isPastItem ? 'text-gray-500' : 'text-aalaw-red'}`}>
            {formatTime(apt.startTime)}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-medium truncate ${isPastItem ? 'text-gray-600' : 'text-gray-900'}`}>{apt.title}</p>
          {apt.location && (
            <p className={`text-xs truncate flex items-center gap-1 mt-0.5 ${isPastItem ? 'text-gray-400' : 'text-gray-500'}`}>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              {apt.location}
            </p>
          )}
        </div>
        <svg className={`w-4 h-4 flex-shrink-0 ${isPastItem ? 'text-gray-300' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 flex flex-col lg:h-[calc(100vh-10rem)] lg:min-h-[300px]">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">Calendar</h1>
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm font-medium text-aalaw-red hover:bg-red-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>
      </div>

      {/* Main content - two column on desktop, stacked on mobile */}
      <div className="flex-1 flex flex-col lg:flex-row lg:min-h-0 lg:overflow-hidden">
        {/* Left column - Appointments list (on desktop) */}
        <div className="lg:w-80 lg:border-r border-gray-200 flex flex-col order-2 lg:order-1 lg:flex-none lg:min-h-0">
          <div className="lg:flex-1 lg:overflow-y-auto lg:overscroll-contain scrollbar-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Video Conferences Section */}
            <VideoConferencesSection maxItems={3} className="p-4 border-b border-gray-200" />

            {/* Selected Day */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatSelectedDate(selectedDate)}
                {selectedDateAppointments.length > 0 && (
                  <span className="px-2 py-0.5 bg-aalaw-red text-white text-xs rounded-full">{selectedDateAppointments.length}</span>
                )}
              </h3>

              {selectedDateAppointments.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No appointments on this day
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedDateAppointments.map(apt => (
                    <button
                      key={apt.id}
                      onClick={() => setSelectedAppointment(apt)}
                      className="w-full text-left p-3 bg-white hover:bg-gray-100 rounded-lg transition-colors shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 text-center">
                          <div className="text-sm font-semibold text-aalaw-red">{formatTime(apt.startTime)}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{apt.title}</p>
                          {apt.location && (
                            <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              </svg>
                              {apt.location}
                            </p>
                          )}
                        </div>
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Appointments */}
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Upcoming
                {upcoming.length > 0 && (
                  <span className="px-2 py-0.5 bg-aalaw-red text-white text-xs rounded-full">{upcoming.length}</span>
                )}
              </h3>

              {upcoming.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm">No upcoming appointments</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {upcoming.map(apt => renderAppointmentItem(apt, false))}
                </div>
              )}
            </div>

            {/* Past Appointments - Collapsible */}
            {past.length > 0 && (
              <div className="p-4 pt-0">
                <button
                  onClick={() => setPastExpanded(!pastExpanded)}
                  className="w-full flex items-center justify-between py-3 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Past
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">{past.length}</span>
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform ${pastExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {pastExpanded && (
                  <div className="space-y-2 mt-2">
                    {past.map(apt => renderAppointmentItem(apt, true))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right column - Calendar (on desktop) */}
        <div className="lg:flex-1 border-b lg:border-b-0 border-gray-200 order-1 lg:order-2">
          <div className="p-4 h-full flex flex-col">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Previous month"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-base font-semibold text-gray-900">
                {currentMonth.toLocaleDateString([], { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Next month"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 lg:flex-1">
              {calendarDays.map((date, idx) => {
                if (!date) {
                  return <div key={`empty-${idx}`} className="aspect-square lg:aspect-auto lg:min-h-[40px]" />;
                }

                const dayAppointments = getAppointmentsForDate(date);
                const hasAppointments = dayAppointments.length > 0;

                return (
                  <button
                    key={date.toISOString()}
                    onClick={() => setSelectedDate(date)}
                    className={`aspect-square lg:aspect-auto lg:min-h-[40px] flex flex-col items-center justify-center rounded-lg text-sm transition-colors relative ${
                      isSelected(date)
                        ? 'bg-aalaw-red text-white'
                        : isToday(date)
                        ? 'bg-red-50 text-aalaw-red font-semibold'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span>{date.getDate()}</span>
                    {hasAppointments && (
                      <div className={`absolute bottom-1 flex gap-0.5 ${isSelected(date) ? 'opacity-80' : ''}`}>
                        {dayAppointments.slice(0, 3).map((_, i) => (
                          <div
                            key={i}
                            className={`w-1 h-1 rounded-full ${isSelected(date) ? 'bg-white' : 'bg-aalaw-red'}`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
        />
      )}
    </div>
  );
}

interface AppointmentDetailModalProps {
  appointment: Appointment;
  onClose: () => void;
}

function AppointmentDetailModal({ appointment, onClose }: AppointmentDetailModalProps) {
  const startTime = new Date(appointment.startTime);
  const endTime = new Date(appointment.endTime);
  const now = new Date();
  const isPast = endTime < now;
  const isOngoing = startTime <= now && endTime >= now;

  const duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatFullDate = (date: Date) => {
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isPast ? 'bg-gray-50' : isOngoing ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isPast ? 'bg-gray-200 text-gray-500' : isOngoing ? 'bg-green-500 text-white' : 'bg-aalaw-red text-white'
            }`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Appointment</h2>
              {isPast && <span className="text-xs text-gray-500">Past</span>}
              {isOngoing && <span className="text-xs text-green-600 font-medium">Happening now</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-full transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Title */}
          <div className={`rounded-lg p-4 ${isPast ? 'bg-gray-50' : 'bg-red-50'}`}>
            <h3 className={`text-lg font-bold ${isPast ? 'text-gray-600' : 'text-gray-900'}`}>
              {appointment.title}
            </h3>
            {appointment.description && (
              <p className={`mt-2 text-sm ${isPast ? 'text-gray-500' : 'text-gray-600'}`}>
                {appointment.description}
              </p>
            )}
          </div>

          {/* Details */}
          <div className="space-y-3">
            {/* Date & Time */}
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-900">{formatFullDate(startTime)}</p>
                <p className="text-sm text-gray-500">
                  {formatTime(startTime)} - {formatTime(endTime)} ({duration} min)
                </p>
              </div>
            </div>

            {/* Location */}
            {(appointment.location || appointment.locationName) && (
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <div>
                  {appointment.locationName && <p className="text-sm font-medium text-gray-900">{appointment.locationName}</p>}
                  {appointment.location && <p className="text-sm text-gray-500">{appointment.location}</p>}
                </div>
              </div>
            )}

            {/* Case */}
            {appointment.case && (
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">Case #{appointment.case.caseNumber}</p>
                  {appointment.case.caseType && <p className="text-sm text-gray-500">{appointment.case.caseType}</p>}
                </div>
              </div>
            )}

            {/* Attendees */}
            {appointment.attendees && appointment.attendees.length > 0 && (
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-2">Attendees</p>
                  <div className="flex flex-wrap gap-2">
                    {appointment.attendees.map((attendee, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-full">
                        <div className="w-6 h-6 rounded-full bg-aalaw-red/10 flex items-center justify-center text-aalaw-red text-xs font-medium">
                          {attendee.firstName[0]}{attendee.lastName[0]}
                        </div>
                        <span className="text-sm text-gray-700">{attendee.firstName} {attendee.lastName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
